import { useState } from "react";
import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ARTICLES_DATA } from "../data/articlesData";

export default function InsightsPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", ...new Set(ARTICLES_DATA.map(a => a.category))];

  const filteredArticles = selectedCategory === "All" 
    ? ARTICLES_DATA 
    : ARTICLES_DATA.filter(a => a.category === selectedCategory);

  const getCategoryColor = (category) => {
    const colors = {
      "Business Strategy": "#dc2626",
      "Cost Optimization": "#16a34a",
      "Compliance": "#3b82f6",
      "IT Services": "#7c3aed",
      "Manpower": "#ea580c"
    };
    return colors[category] || "#64748b";
  };

  return (
    <div>
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block font-bold text-xs tracking-[0.2em] uppercase text-red-600 mb-3"
          >
            Insights & Resources
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6 font-heading"
          >
            Expert Insights for Your Business
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 max-w-2xl mx-auto"
          >
            Practical guides, industry trends, and expert tips to help you make smarter vendor decisions
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-y border-slate-100 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-bold text-sm whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? "bg-red-600 text-white shadow-lg shadow-red-600/30"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <ArticleCard
                key={article.id}
                article={article}
                index={index}
                navigate={navigate}
                getCategoryColor={getCategoryColor}
              />
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ArticleCard({ article, index, navigate, getCategoryColor }) {
  const [isHovered, setIsHovered] = useState(false);
  const categoryColor = getCategoryColor(article.category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        navigate(`/insights/${article.slug}`);
        window.scrollTo(0, 0);
      }}
      className="bg-white rounded-2xl overflow-hidden border border-slate-200 cursor-pointer transition-all duration-300 group"
      style={{
        boxShadow: isHovered ? `0 20px 40px ${categoryColor}20` : "0 4px 12px rgba(0,0,0,0.08)",
        transform: isHovered ? "translateY(-8px)" : "none",
      }}
    >
      {/* Article Image */}
      <div className="relative h-56 overflow-hidden bg-slate-100">
        <img
          src={article.image}
          alt={article.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div
          className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg"
          style={{ background: `${categoryColor}dd` }}
        >
          {article.category}
        </div>
      </div>

      {/* Article Content */}
      <div className="p-6">
        <h3 className="text-xl font-extrabold text-slate-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3">
          {article.description}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 font-semibold">
            <Clock size={14} />
            {article.readTime}
          </div>
          <div
            className={`flex items-center gap-1 text-sm font-bold transition-all ${
              isHovered ? "gap-2" : "gap-1"
            }`}
            style={{ color: categoryColor }}
          >
            Read More
            <ArrowRight size={16} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
